import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageTransition from '../components/PageTransition';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiMail, FiCheck, FiX, FiSettings, FiClock, FiCalendar, FiRefreshCw, FiChevronUp, FiAlertCircle, FiUser, FiLogOut, FiBell, FiEdit2, FiLock, FiStar } from 'react-icons/fi';
import { FaMicrosoft, FaGoogle } from 'react-icons/fa';
import { FiLink, FiPlus, FiTrash2 } from 'react-icons/fi';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { isNative } from '../services/platformService';
import { openManagementUrl } from '../services/revenueCatService';
import { checkIsSuperAdmin } from '../services/adminService';
import {
  checkPushPermission,
  requestPushPermission,
  registerForPush,
  savePushTokenToServer,
  removeAllUserPushTokens,
  hasActivePushTokens,
  sendTestPushNotification
} from '../services/pushNotificationService';
import SafeIcon from '../common/SafeIcon';
import HelpTooltip from '../components/HelpTooltip';
import { getAvailabilityPreference, setAvailabilityPreference, getWeekStartDay, setWeekStartDay } from '../services/userPreferencesService';
import NotificationPreferencesSection from '../components/NotificationPreferencesSection';
import AppearanceSection from '../components/AppearanceSection';
import LocationSection from '../components/LocationSection';
import { trackAvailabilityPrefSetAvailable, trackAvailabilityPrefSetBusy } from '../services/analytics';
import { connectCalendar, getCalendarSyncStatus, disconnectCalendar, syncCalendarToAvailability, updateCalendarSyncSettings } from '../services/calendarSyncService';
import { getIcsSubscriptions, addIcsSubscription, removeIcsSubscription, syncIcsCalendar, getIcsUrlHints } from '../services/icsCalendarService';
import { calendarSyncConfig } from '../services/msalConfig';
import { useToast } from '../contexts/ToastContext';

/**
 * A single editable profile row. Read-only by default with a pencil icon;
 * tap pencil → swaps to an inline input with Save / Cancel.
 */
function ProfileFieldRow({
  label,
  field,
  value,
  placeholder,
  inputType = 'text',
  inputMode,
  sanitise,
  editingField,
  draftValue,
  setDraftValue,
  savingField,
  onEdit,
  onSave,
  onCancel
}) {
  const isEditing = editingField === field;
  const isSaving = savingField === field;
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <div className="flex items-center justify-between py-3 gap-3">
        <span className="text-sm font-medium text-text-secondary flex-shrink-0">{label}</span>
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <input
            ref={inputRef}
            type={inputType}
            inputMode={inputMode}
            value={draftValue}
            disabled={isSaving}
            onChange={(e) => {
              const next = sanitise ? sanitise(e.target.value) : e.target.value;
              setDraftValue(next);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSave(field);
              if (e.key === 'Escape') onCancel();
            }}
            placeholder={placeholder}
            className="min-w-0 flex-1 max-w-xs px-4 py-2.5 bg-surface border border-border-subtle rounded-lg text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
          />
          <button
            onClick={() => onSave(field)}
            disabled={isSaving}
            aria-label={`Save ${label}`}
            className="px-3 py-2 text-sm min-h-[36px] bg-accent text-white rounded-lg hover:bg-interactive-hover transition-colors flex items-center justify-center disabled:opacity-50"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <SafeIcon icon={FiCheck} className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={onCancel}
            disabled={isSaving}
            aria-label={`Cancel editing ${label}`}
            className="px-3 py-2 text-sm min-h-[36px] border border-border-subtle text-text-secondary rounded-lg hover:bg-surface transition-colors flex items-center justify-center disabled:opacity-50"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-3 gap-4">
      <span className="text-sm font-medium text-text-secondary flex-shrink-0">{label}</span>
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-text-primary truncate">
          {value || <span className="text-text-muted">Not set</span>}
        </span>
        <button
          onClick={() => onEdit(field)}
          aria-label={`Edit ${label}`}
          className="p-3 -m-1 text-text-muted hover:text-text-primary transition-colors flex items-center justify-center"
          style={{ minWidth: '44px', minHeight: '44px' }}
        >
          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function Settings() {
  const { user, signOut, updateProfile, isPro, plan, planPeriodEnd, planSource } = useAuth();
  const isOnNative = Capacitor.isNativePlatform();
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [manageInfoOpen, setManageInfoOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Ref to track if we've handled the redirect sync
  const redirectSyncHandledRef = useRef(false);

  // Availability preference state
  const [availabilityPreference, setAvailabilityPreferenceState] = useState('available');

  // Week-start preference: 0 = Sunday, 1 = Monday. Defaults to Monday until
  // the user's stored preference loads.
  const [weekStartsOn, setWeekStartsOnState] = useState(1);

  // Loading state for preferences
  const [preferencesLoading, setPreferencesLoading] = useState(true);

  // Calendar connection state
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(null); // null or 'outlook' or 'google'
  const [calendarSyncing, setCalendarSyncing] = useState(false);
  const [calendarError, setCalendarError] = useState('');
  const [calendarSuccess, setCalendarSuccess] = useState('');
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);
  const [syncSettings, setSyncSettings] = useState(calendarSyncConfig);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // ICS subscription state
  const [icsSubscriptions, setIcsSubscriptions] = useState([]);
  const [showIcsModal, setShowIcsModal] = useState(false);
  const [showIcsMenu, setShowIcsMenu] = useState(false);
  const [showConnectedIcsCalendars, setShowConnectedIcsCalendars] = useState(false);
  const [icsName, setIcsName] = useState('');
  const [icsUrl, setIcsUrl] = useState('');
  const [icsLoading, setIcsLoading] = useState(false);
  const [icsSyncing, setIcsSyncing] = useState(null); // subscriptionId or null

  // Push notification state (native only)
  const [pushStatus, setPushStatus] = useState('checking'); // 'checking', 'granted', 'denied', 'prompt', 'unavailable'
  const [pushLoading, setPushLoading] = useState(false);

  // Profile form state - per-field inline edit pattern
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [editingField, setEditingField] = useState(null); // 'firstName' | 'lastName' | null
  const [draftValue, setDraftValue] = useState('');
  const [savingField, setSavingField] = useState(null);

  // Load user preferences from database
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) return;

      setPreferencesLoading(true);
      try {
        const [preferenceResult, weekStartResult] = await Promise.all([
          getAvailabilityPreference(),
          getWeekStartDay()
        ]);

        setAvailabilityPreferenceState(preferenceResult || 'available');
        setWeekStartsOnState(weekStartResult);
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        setPreferencesLoading(false);
      }
    };

    loadPreferences();
  }, [user]);

  // Initialize profile data from user object
  useEffect(() => {
    if (!user) return;
    const initial = {
      firstName: user?.user_metadata?.first_name || '',
      lastName: user?.user_metadata?.last_name || '',
      email: user?.email || '',
    };
    setProfileFormData(initial);
  }, [user]);

  // Load calendar sync status
  useEffect(() => {
    const loadCalendarStatus = async () => {
      if (!user) return;

      try {
        const status = await getCalendarSyncStatus();
        setCalendarStatus(status);

        // Load sync settings if connected
        if (status?.connected && status?.syncSettings) {
          setSyncSettings({
            enabled: status.syncEnabled !== false,
            excludeAllDayEvents: status.syncSettings.excludeAllDayEvents || false,
            onlySyncBusyEvents: status.syncSettings.onlySyncBusyEvents !== false,
            syncDaysAhead: status.syncSettings.syncDaysAhead || 180,
            addRoundsToCalendar: status.syncSettings.addRoundsToCalendar !== false
          });
        } else {
          setSyncSettings({
            enabled: true,
            excludeAllDayEvents: false,
            onlySyncBusyEvents: true,
            syncDaysAhead: 180,
            addRoundsToCalendar: true
          });
        }
      } catch (error) {
        console.error('Error loading calendar status:', error);
      }
    };

    loadCalendarStatus();
  }, [user]);

  // Load ICS subscriptions
  useEffect(() => {
    const loadIcsSubscriptions = async () => {
      if (!user) return;

      try {
        const subscriptions = await getIcsSubscriptions();
        setIcsSubscriptions(subscriptions);
      } catch (error) {
        console.error('Error loading ICS subscriptions:', error);
      }
    };

    loadIcsSubscriptions();
  }, [user]);

  // Check push notification permission status (native only)
  useEffect(() => {
    const checkPushStatus = async () => {
      if (!isNative()) {
        setPushStatus('unavailable');
        return;
      }

      try {
        const permission = await checkPushPermission();

        if (permission === 'granted') {
          // Permission is granted, but check if we have active tokens
          const hasTokens = await hasActivePushTokens();
          setPushStatus(hasTokens ? 'granted' : 'disabled');
        } else {
          setPushStatus(permission);
        }
      } catch (error) {
        console.error('Error checking push permission:', error);
        setPushStatus('unavailable');
      }
    };

    checkPushStatus();
  }, []);

  // Handle redirect flow from OAuth callback - trigger initial sync
  useEffect(() => {
    const handleRedirectSync = async () => {
      // Check if we arrived from OAuth callback redirect (mobile/PWA flow)
      const state = location.state;
      if (!state?.calendarConnected || redirectSyncHandledRef.current) {
        return;
      }

      // Mark as handled to prevent duplicate syncs
      redirectSyncHandledRef.current = true;

      // Clear the location state to prevent re-triggering on refresh
      navigate(location.pathname, { replace: true, state: {} });

      console.log('📅 Handling redirect flow calendar sync for:', state.calendarProvider);
      setCalendarSuccess(`Calendar connected (${state.calendarEmail})! Syncing events...`);
      setCalendarSyncing(true);

      try {
        // Reload calendar status first
        const status = await getCalendarSyncStatus();
        setCalendarStatus(status);

        // Trigger initial sync
        const today = new Date();
        const endDate = new Date();
        endDate.setDate(today.getDate() + 180); // Sync next 180 days

        const syncResult = await syncCalendarToAvailability(
          today.toISOString().split('T')[0],
          endDate.toISOString().split('T')[0]
        );

        if (syncResult.success) {
          setCalendarSuccess(`Connected and synced ${syncResult.eventsProcessed || 0} calendar events`);
          // Reload status to show last synced time
          const updatedStatus = await getCalendarSyncStatus();
          setCalendarStatus(updatedStatus);
        } else {
          setCalendarSuccess('Calendar connected but sync failed. Try syncing manually.');
          setCalendarError(syncResult.error || 'Sync failed');
        }
      } catch (error) {
        console.error('Redirect sync error:', error);
        setCalendarError('Calendar connected but sync failed. Try syncing manually.');
      } finally {
        setCalendarSyncing(false);
      }
    };

    if (user && location.state?.calendarConnected) {
      handleRedirectSync();
    }
  }, [user, location.state, navigate]);

  // Clear calendar messages after delay
  useEffect(() => {
    if (calendarError || calendarSuccess) {
      const timer = setTimeout(() => {
        setCalendarError('');
        setCalendarSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [calendarError, calendarSuccess]);

  // Close calendar menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if ((showCalendarMenu || showIcsMenu) && !event.target.closest('.relative')) {
        setShowCalendarMenu(false);
        setShowIcsMenu(false);
      }
    };

    if (showCalendarMenu || showIcsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCalendarMenu, showIcsMenu]);

  const handleWeekStartChange = async (newValue) => {
    // newValue: 0 (Sunday) or 1 (Monday)
    try {
      setWeekStartsOnState(newValue);
      await setWeekStartDay(newValue);
      const label = newValue === 0 ? 'Sunday' : 'Monday';
      toast.success(`Week now starts on ${label}`);
    } catch (error) {
      console.error('Error saving week start:', error);
      toast.error('Failed to save week start preference');
    }
  };

  const handleAvailabilityPreferenceChange = async (newPreference) => {
    try {
      setAvailabilityPreferenceState(newPreference);
      await setAvailabilityPreference(newPreference);

      // Track preference change
      if (user?.id) {
        if (newPreference === 'available') {
          trackAvailabilityPrefSetAvailable(user.id);
        } else if (newPreference === 'unavailable') {
          trackAvailabilityPrefSetBusy(user.id);
        }
      }

    } catch (error) {
      console.error('Error saving availability preference:', error);
      toast.error('Failed to save availability preference. Please try again.');
    }
  };

  // Profile per-field edit handlers
  const handleFieldEdit = (field) => {
    setEditingField(field);
    setDraftValue(profileFormData[field] || '');
  };

  const handleFieldCancel = () => {
    setEditingField(null);
    setDraftValue('');
  };

  const handleFieldSave = async (field) => {
    const previousValue = profileFormData[field];
    const newValue = draftValue;

    // Optimistic update
    const updated = { ...profileFormData, [field]: newValue };
    setProfileFormData(updated);
    setSavingField(field);

    try {
      const { error } = await updateProfile(updated);

      if (error) {
        // Roll back on error
        setProfileFormData({ ...profileFormData, [field]: previousValue });
        console.error('Error updating profile:', error);
        toast.error('Failed to update. Please try again.');
        return;
      }

      setEditingField(null);
      setDraftValue('');
      toast.success('Updated');
    } catch (error) {
      setProfileFormData({ ...profileFormData, [field]: previousValue });
      console.error('Error saving profile:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setSavingField(null);
    }
  };

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
      navigate('/');
    }
  };

  // Account deletion (Apple 5.1.1(v)). Opens a confirmation modal that
  // requires the user to type DELETE before the destructive call is enabled.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE' || deletingAccount) return;
    setDeletingAccount(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase.functions.invoke('delete-account');
      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Failed to delete account');
      }
      // Don't wait for sign-out to finish - the auth.users row is already
      // gone so the next refresh attempt will land on /login anyway.
      await signOut().catch(() => {});
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[Settings] delete account failed:', err);
      toast.error(err?.message || 'Could not delete your account. Please try again or contact support.');
      setDeletingAccount(false);
    }
  };

  // Calendar connection handlers
  const handleCalendarConnect = async (provider = 'outlook') => {
    setCalendarLoading(provider);
    setCalendarError('');
    setCalendarSuccess('');

    try {
      const result = await connectCalendar(provider);
      if (result.success) {
        setCalendarSuccess('Calendar connected! Syncing events...');

        // Reload calendar status
        const status = await getCalendarSyncStatus();
        setCalendarStatus(status);

        // Automatically sync calendar events after connection
        const today = new Date();
        const endDate = new Date();
        endDate.setDate(today.getDate() + 180); // Sync next 180 days (6 months)

        const syncResult = await syncCalendarToAvailability(
          today.toISOString().split('T')[0],
          endDate.toISOString().split('T')[0]
        );

        if (syncResult.success) {
          setCalendarSuccess(`Connected and synced ${syncResult.eventsProcessed || 0} calendar events`);
          // Reload status to show last synced time
          const updatedStatus = await getCalendarSyncStatus();
          setCalendarStatus(updatedStatus);
        } else {
          setCalendarSuccess('Calendar connected but sync failed. Try syncing manually.');
          setCalendarError(syncResult.error || 'Sync failed');
        }
      } else {
        setCalendarError(result.error || 'Failed to connect calendar');
      }
    } catch (error) {
      setCalendarError('An error occurred while connecting');
      console.error('Calendar connection error:', error);
    } finally {
      setCalendarLoading(null);
    }
  };

  const handleCalendarDisconnect = async () => {
    setShowCalendarMenu(false);
    const provider = calendarStatus?.provider || 'outlook';
    setCalendarLoading(provider);
    try {
      const result = await disconnectCalendar(provider);
      if (result.success) {
        setCalendarSuccess('Calendar disconnected');
        setCalendarStatus(null);
      } else {
        setCalendarError(result.error || 'Failed to disconnect');
      }
    } catch (error) {
      setCalendarError('An error occurred while disconnecting');
    } finally {
      setCalendarLoading(null);
    }
  };

  const handleCalendarSync = async () => {
    setShowCalendarMenu(false);
    setCalendarSyncing(true);
    setCalendarError('');
    setCalendarSuccess('');

    try {
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(today.getDate() + syncSettings.syncDaysAhead); // Use setting

      const syncResult = await syncCalendarToAvailability(
        today.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      );

      if (syncResult.success) {
        setCalendarSuccess(`Successfully synced ${syncResult.eventsProcessed || 0} calendar events`);
        // Reload status to show last synced time
        const updatedStatus = await getCalendarSyncStatus();
        setCalendarStatus(updatedStatus);
      } else {
        setCalendarError(syncResult.error || 'Sync failed. Please try again.');
      }
    } catch (error) {
      setCalendarError('An error occurred during sync. Please try again.');
      console.error('Calendar sync error:', error);
    } finally {
      setCalendarSyncing(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setCalendarError('');
    setCalendarSuccess('');

    try {
      await updateCalendarSyncSettings(syncSettings);
      setCalendarSuccess('Settings saved successfully');
      setShowCalendarSettings(false);

      // Reload calendar status to get updated settings
      const updatedStatus = await getCalendarSyncStatus();
      setCalendarStatus(updatedStatus);
    } catch (error) {
      setCalendarError('Failed to save settings. Please try again.');
      console.error('Error saving settings:', error);
    } finally {
      setSettingsSaving(false);
    }
  };

  // ICS subscription handlers
  const handleAddIcsSubscription = async () => {
    if (!icsName.trim() || !icsUrl.trim()) {
      toast.error('Please enter both a name and URL');
      return;
    }

    setIcsLoading(true);
    setCalendarError('');
    setCalendarSuccess('');

    try {
      const result = await addIcsSubscription(icsName, icsUrl);

      if (result.success) {
        setCalendarSuccess(result.message);
        setShowIcsModal(false);
        setIcsName('');
        setIcsUrl('');
        // Reload subscriptions
        const subscriptions = await getIcsSubscriptions();
        setIcsSubscriptions(subscriptions);
      } else {
        setCalendarError(result.error || 'Failed to add calendar');
      }
    } catch (error) {
      setCalendarError('An error occurred while adding the calendar');
      console.error('Error adding ICS subscription:', error);
    } finally {
      setIcsLoading(false);
    }
  };

  const handleRemoveIcsSubscription = async (subscriptionId, name) => {
    if (!confirm(`Are you sure you want to remove "${name}"? This will also remove synced availability from this calendar.`)) {
      return;
    }

    setIcsLoading(true);
    setCalendarError('');

    try {
      const result = await removeIcsSubscription(subscriptionId);

      if (result.success) {
        setCalendarSuccess(`Removed "${name}" calendar`);
        // Reload subscriptions
        const subscriptions = await getIcsSubscriptions();
        setIcsSubscriptions(subscriptions);
      } else {
        setCalendarError(result.error || 'Failed to remove calendar');
      }
    } catch (error) {
      setCalendarError('An error occurred while removing the calendar');
      console.error('Error removing ICS subscription:', error);
    } finally {
      setIcsLoading(false);
    }
  };

  const handleSyncIcsCalendar = async (subscriptionId) => {
    setIcsSyncing(subscriptionId);
    setCalendarError('');
    setCalendarSuccess('');

    try {
      const result = await syncIcsCalendar(subscriptionId);

      if (result.success) {
        setCalendarSuccess(`Synced ${result.eventsProcessed || 0} events`);
        // Reload subscriptions to update last_synced_at
        const subscriptions = await getIcsSubscriptions();
        setIcsSubscriptions(subscriptions);
      } else {
        setCalendarError(result.error || 'Sync failed');
      }
    } catch (error) {
      setCalendarError('An error occurred during sync');
      console.error('Error syncing ICS calendar:', error);
    } finally {
      setIcsSyncing(null);
    }
  };

  // Push notification handlers
  const handleEnablePushNotifications = async () => {
    console.log('[Settings] handleEnablePushNotifications called');
    setPushLoading(true);
    try {
      // Request permission
      console.log('[Settings] Requesting push permission...');
      const permission = await requestPushPermission();
      console.log('[Settings] Permission result:', permission);

      if (permission === 'granted' || permission === true) {
        setPushStatus('granted');
        // Register for push and save token
        console.log('[Settings] Permission granted, registering for push...');
        const token = await registerForPush();
        console.log('[Settings] registerForPush returned token:', token ? token.substring(0, 20) + '...' : 'null');
        if (token) {
          console.log('[Settings] Saving token to server...');
          const result = await savePushTokenToServer(token);
          console.log('[Settings] savePushTokenToServer result:', result);
          if (result.success) {
            // Send a test push notification to confirm everything works
            console.log('[Settings] Token saved, sending test notification...');
            const testResult = await sendTestPushNotification(token);
            console.log('[Settings] sendTestPushNotification result:', testResult);
            if (!testResult.success) {
              // Test notification failed, but token was saved - show toast as fallback
              console.warn('[Settings] Test notification failed:', testResult.error);
              toast.success('Push notifications enabled');
            }
            // If test notification succeeded, the user will see a push notification
            // confirming that notifications are now enabled - no toast needed
          } else {
            console.error('[Settings] Failed to save push token:', result.error);
            toast.error('Push enabled but failed to register with server');
          }
        } else {
          // Registration timed out - likely simulator or missing entitlement
          toast.error('Could not register with Apple Push service. Please ensure you are on a real device.');
          setPushStatus('disabled');
        }
      } else if (permission === 'denied' || permission === false) {
        setPushStatus('denied');
        toast.error('Push notifications denied. Please enable in your device settings.');
      } else {
        setPushStatus(permission);
      }
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      toast.error('Failed to enable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePushNotifications = async () => {
    setPushLoading(true);
    try {
      // Remove all push tokens for this user from the server
      const result = await removeAllUserPushTokens();
      if (result.success) {
        // Set status to 'disabled' to show the toggle is off
        // Note: OS permission is still granted, but we won't send notifications
        setPushStatus('disabled');
        toast.success('Push notifications disabled');
      } else {
        console.error('Failed to remove push tokens:', result.error);
        toast.error('Failed to disable push notifications');
      }
    } catch (error) {
      console.error('Error disabling push notifications:', error);
      toast.error('Failed to disable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  // Version footer + 7-tap reveal for an "Admin" link. The link only ever
  // surfaces for super-admins; /admin is independently gated by AdminRoute,
  // so the tap pattern is a discoverability gate, not a security one.
  const [versionInfo, setVersionInfo] = useState({ version: '1.1.0', build: null });
  const [versionTapCount, setVersionTapCount] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adminVisible, setAdminVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    CapApp.getInfo()
      .then((info) => {
        if (!cancelled) setVersionInfo({ version: info.version, build: info.build });
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    checkIsSuperAdmin().then(setIsSuperAdmin).catch(() => setIsSuperAdmin(false));
  }, []);

  const handleVersionTap = () => {
    if (!isSuperAdmin) return;
    setVersionTapCount((c) => {
      const next = c + 1;
      if (next >= 7) setAdminVisible(true);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-app">
      <SEO title="Settings | TeeSync" noindex={true} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageTransition className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-12 xl:gap-16 lg:items-start">
          {/* Calendar Success Message */}
          {calendarSuccess && (
            <div className="bg-status-success/10 border border-status-success/30 text-status-success p-3 rounded-lg flex items-center lg:col-span-2">
              <SafeIcon icon={FiCheck} className="w-4 h-4 mr-3 flex-shrink-0" />
              <p className="text-sm">{calendarSuccess}</p>
            </div>
          )}

          {/* Calendar Error Message */}
          {calendarError && (
            <div className="bg-status-error/10 border border-status-error/30 text-status-error p-3 rounded-lg flex items-center lg:col-span-2">
              <SafeIcon icon={FiX} className="w-4 h-4 mr-3 flex-shrink-0" />
              <p className="text-sm">{calendarError}</p>
            </div>
          )}

          {/* Left column on desktop: Profile + Availability. On mobile/tablet this wrapper is just a vertical stack. */}
          <div className="space-y-6">

          {/* Profile Information Section */}
          <div>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-text-primary">Profile</h3>
              <button
                onClick={handleSignOut}
                className="flex items-center justify-center px-3 py-1.5 text-status-error rounded-md hover:bg-status-error/10 transition-colors font-medium text-sm"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-1.5" />
                Sign Out
              </button>
            </div>

            <div className="divide-y divide-border-subtle">
              {/* First Name */}
              <ProfileFieldRow
                label="First Name"
                field="firstName"
                value={profileFormData.firstName}
                placeholder="First name"
                editingField={editingField}
                draftValue={draftValue}
                setDraftValue={setDraftValue}
                savingField={savingField}
                onEdit={handleFieldEdit}
                onSave={handleFieldSave}
                onCancel={handleFieldCancel}
              />

              {/* Last Name */}
              <ProfileFieldRow
                label="Last Name"
                field="lastName"
                value={profileFormData.lastName}
                placeholder="Last name"
                editingField={editingField}
                draftValue={draftValue}
                setDraftValue={setDraftValue}
                savingField={savingField}
                onEdit={handleFieldEdit}
                onSave={handleFieldSave}
                onCancel={handleFieldCancel}
              />

              {/* Email - read only */}
              <div className="flex items-center justify-between py-3 gap-4">
                <span className="text-sm font-medium text-text-secondary flex-shrink-0">Email</span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-text-primary truncate">
                    {profileFormData.email || <span className="text-text-muted">Not set</span>}
                  </span>
                  <SafeIcon icon={FiLock} className="text-text-muted w-4 h-4 flex-shrink-0" aria-label="Email cannot be changed" />
                </div>
              </div>

            </div>
          </div>

          {/* Plan Section - shows current subscription tier and the right
              billing surface for the user's purchase platform. Free users see
              the upgrade CTA (kept high on the page so App Review can reach
              IAPs in two taps). Pro users get a platform-aware Manage button:
                • plan_source='ios'    → opens Apple's manage-subs sheet
                • plan_source='stripe' → opens RC's hosted Stripe portal
                • plan_source='manual' → contact-support note (no button)
              When the user opens the wrong surface (e.g. an iOS-purchased
              account on web), we show an explanatory banner instead of a
              broken button. We never roll our own cancel flow - Apple
              requires the App Store sheet for IAPs and FTC click-to-cancel
              rules require parity on web. */}
          <div>
            <h3 className="text-lg font-bold text-text-primary mb-5">Plan</h3>
            <div className="rounded-xl border-2 border-accent/20 bg-accent/5 p-4">
              {(() => {
                const isIosProOnDevice = isPro && planSource === 'ios' && isOnNative;
                // Only need bottom margin when something else renders below
                // the row (e.g. the Upgrade button or a Stripe Manage button).
                // For the iOS-Pro case (modal only) it would just push the row
                // off-centre inside the card.
                const hasContentBelow = !isPro
                  || (isPro && planSource === 'manual')
                  || (isPro && planSource === 'ios' && !isOnNative)
                  || (isPro && planSource === 'stripe' && isOnNative)
                  || (isPro && ((planSource === 'stripe' && !isOnNative) || !planSource));
                const RowEl = isIosProOnDevice ? 'button' : 'div';
                const rowProps = isIosProOnDevice
                  ? { type: 'button', onClick: () => setManageInfoOpen(true), 'aria-label': 'Manage subscription info' }
                  : {};
                return (
                  <RowEl
                    {...rowProps}
                    className={`w-full text-left flex items-center justify-between ${hasContentBelow ? 'mb-3' : ''} ${isIosProOnDevice ? 'cursor-pointer active:opacity-70 transition-opacity' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiStar} className="w-5 h-5 text-accent" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-base font-semibold text-text-primary">
                          {isPro ? 'TeeSync Pro' : 'TeeSync Free'}
                        </p>
                        <p className="text-xs text-text-secondary">
                          {isPro
                            ? (planPeriodEnd
                              ? `Renews ${new Date(planPeriodEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                              : 'Active subscription')
                            : 'Unlock unlimited everything'}
                        </p>
                      </div>
                    </div>
                  </RowEl>
                );
              })()}

              {!isPro && (
                <button
                  onClick={() => navigate('/upgrade')}
                  className="w-full px-4 py-3 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-interactive-hover transition-colors"
                >
                  Upgrade to TeeSync Pro
                </button>
              )}

              {isPro && planSource === 'manual' && (
                <p className="text-xs text-text-secondary leading-relaxed">
                  Your Pro access was granted manually. Contact support to make changes.
                </p>
              )}

              {isPro && planSource === 'ios' && !isOnNative && (
                <p className="text-xs text-text-secondary leading-relaxed">
                  Your subscription was purchased on iOS. Manage it from the TeeSync iOS app or at{' '}
                  <a
                    href="https://apps.apple.com/account/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent underline"
                  >
                    apps.apple.com/account/subscriptions
                  </a>.
                </p>
              )}

              {isPro && planSource === 'stripe' && isOnNative && (
                <p className="text-xs text-text-secondary leading-relaxed">
                  Your subscription was purchased on the web. Manage it at teesync.app from a browser.
                </p>
              )}

              {isPro && (
                (planSource === 'stripe' && !isOnNative) ||
                (!planSource)
              ) && (
                <button
                  onClick={async () => {
                    setManagingSubscription(true);
                    try {
                      const opened = await openManagementUrl();
                      if (!opened) {
                        toast.info('Subscription management isn’t available yet - please contact support.');
                      }
                    } finally {
                      setManagingSubscription(false);
                    }
                  }}
                  disabled={managingSubscription}
                  className="w-full px-4 py-3 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-interactive-hover transition-colors disabled:opacity-50"
                >
                  {managingSubscription ? 'Opening…' : 'Manage Subscription'}
                </button>
              )}
            </div>
          </div>

          {/* Manage-subscription info modal (iOS-purchased + on native iPhone) */}
          {manageInfoOpen && (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40"
              onClick={() => setManageInfoOpen(false)}
            >
              <div
                className="w-full max-w-sm bg-card rounded-2xl shadow-card p-5 sm:p-6"
                role="dialog"
                aria-modal="true"
                aria-labelledby="manage-info-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 id="manage-info-title" className="text-base font-bold text-text-primary">
                    Manage your subscription
                  </h4>
                  <button
                    type="button"
                    onClick={() => setManageInfoOpen(false)}
                    className="ml-3 -mt-1 -mr-1 w-8 h-8 rounded-full flex items-center justify-center hover:bg-surface"
                    aria-label="Close"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
                  </button>
                </div>
                <p className="text-sm text-text-secondary leading-relaxed">
                  Open the iPhone <span className="font-semibold">Settings</span> app, tap your name at the top, then <span className="font-semibold">Subscriptions</span>, and choose <span className="font-semibold">TeeSync</span>. From there you can change plan, pause, or cancel.
                </p>
              </div>
            </div>
          )}

          {/* Location Section (home course / country / timezone) */}
          <LocationSection />

          {/* Availability Settings Section */}
          <div>
            <h3 className="text-lg font-bold text-text-primary mb-5">Availability</h3>

            {preferencesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-3 text-text-secondary text-sm">Loading</span>
              </div>
            ) : (
            <div className="space-y-6">
              {/* Row 1: Availability Preference */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-medium text-text-secondary">Selection Mode</h4>
                  <HelpTooltip
                    title="Selection Mode"
                    content="Choose whether you want to select times when you're available to play golf, or block out times when you're not available."
                    position="right"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAvailabilityPreferenceChange('available')}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-center min-h-[80px] sm:min-h-[100px] bg-card ${
                      availabilityPreference === 'available'
                        ? 'border-accent'
                        : 'border-border-subtle'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-center w-full sm:items-center">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center sm:mr-3 mb-1 sm:mb-0 flex-shrink-0 ${
                        availabilityPreference === 'available' ? 'bg-accent' : 'bg-surface'
                      }`}>
                        <SafeIcon icon={FiCheck} className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          availabilityPreference === 'available' ? 'text-white' : 'text-text-muted'
                        }`} />
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <h5 className={`text-sm sm:text-base font-bold whitespace-nowrap ${
                          availabilityPreference === 'available' ? 'text-accent' : 'text-text-primary'
                        }`}>
                          Set Available
                        </h5>
                      </div>
                    </div>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAvailabilityPreferenceChange('unavailable')}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-center min-h-[80px] sm:min-h-[100px] bg-card ${
                      availabilityPreference === 'unavailable'
                        ? 'border-status-error'
                        : 'border-border-subtle'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-center w-full sm:items-center">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center sm:mr-3 mb-1 sm:mb-0 flex-shrink-0 ${
                        availabilityPreference === 'unavailable' ? 'bg-status-error' : 'bg-surface'
                      }`}>
                        <SafeIcon icon={FiX} className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          availabilityPreference === 'unavailable' ? 'text-white' : 'text-text-muted'
                        }`} />
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <h5 className={`text-sm sm:text-base font-bold whitespace-nowrap ${
                          availabilityPreference === 'unavailable' ? 'text-status-error' : 'text-text-primary'
                        }`}>
                          Set Busy
                        </h5>
                      </div>
                    </div>
                  </motion.button>
                </div>
              </div>

              {/* Row 2: Week Start Day */}
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-3">Week starts on</h4>

                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleWeekStartChange(1)}
                    aria-pressed={weekStartsOn === 1}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-center min-h-[80px] sm:min-h-[100px] bg-card ${
                      weekStartsOn === 1 ? 'border-accent' : 'border-border-subtle'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-center w-full sm:items-center">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center sm:mr-3 mb-1 sm:mb-0 flex-shrink-0 ${
                        weekStartsOn === 1 ? 'bg-accent' : 'bg-surface'
                      }`}>
                        <SafeIcon icon={FiCalendar} className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          weekStartsOn === 1 ? 'text-white' : 'text-text-muted'
                        }`} />
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <h5 className={`text-sm sm:text-base font-bold whitespace-nowrap ${
                          weekStartsOn === 1 ? 'text-accent' : 'text-text-primary'
                        }`}>
                          Monday
                        </h5>
                      </div>
                    </div>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleWeekStartChange(0)}
                    aria-pressed={weekStartsOn === 0}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-center min-h-[80px] sm:min-h-[100px] bg-card ${
                      weekStartsOn === 0 ? 'border-accent' : 'border-border-subtle'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-center w-full sm:items-center">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center sm:mr-3 mb-1 sm:mb-0 flex-shrink-0 ${
                        weekStartsOn === 0 ? 'bg-accent' : 'bg-surface'
                      }`}>
                        <SafeIcon icon={FiCalendar} className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          weekStartsOn === 0 ? 'text-white' : 'text-text-muted'
                        }`} />
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <h5 className={`text-sm sm:text-base font-bold whitespace-nowrap ${
                          weekStartsOn === 0 ? 'text-accent' : 'text-text-primary'
                        }`}>
                          Sunday
                        </h5>
                      </div>
                    </div>
                  </motion.button>
                </div>
              </div>
            </div>
            )}
          </div>

          </div>
          {/* Right column on desktop: Calendar Linking + Notifications + Appearance. On mobile/tablet this wrapper is just a vertical stack. */}
          <div className="space-y-6">

          {/* Calendar Settings Section */}
          <div>
            <h3 className="text-lg font-bold text-text-primary mb-5">Calendar Linking</h3>

            <div className="relative">
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  {/* Outlook Calendar Sync Option - Nested Card */}
                  <motion.button
                    whileHover={{ scale: (calendarLoading || calendarSyncing || (calendarStatus?.connected && calendarStatus?.provider === 'google')) ? 1 : 1.02 }}
                    whileTap={{ scale: (calendarLoading || calendarSyncing || (calendarStatus?.connected && calendarStatus?.provider === 'google')) ? 1 : 0.98 }}
                    onClick={() => {
                      if (calendarStatus?.connected && calendarStatus?.provider === 'google') {
                        return;
                      }
                      if (calendarStatus?.connected && calendarStatus?.provider === 'outlook') {
                        setShowCalendarMenu(!showCalendarMenu);
                      } else {
                        handleCalendarConnect('outlook');
                      }
                    }}
                    disabled={calendarLoading || calendarSyncing || (calendarStatus?.connected && calendarStatus?.provider === 'google')}
                    className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-center min-h-[80px] sm:min-h-[100px] bg-card ${
                      calendarStatus?.connected && calendarStatus?.provider === 'outlook'
                        ? 'border-accent'
                        : calendarStatus?.connected && calendarStatus?.provider === 'google'
                          ? 'border-border-subtle opacity-50 cursor-not-allowed'
                          : 'border-border-subtle'
                    } ${(calendarLoading || calendarSyncing) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex flex-col sm:flex-row items-center justify-center">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center sm:mr-3 mb-1 sm:mb-0 flex-shrink-0">
                        <SafeIcon icon={calendarSyncing && calendarStatus?.provider === 'outlook' ? FiRefreshCw : FaMicrosoft} className={`w-6 h-6 sm:w-7 sm:h-7 ${calendarStatus?.connected && calendarStatus?.provider === 'outlook' ? 'text-accent' : calendarStatus?.connected && calendarStatus?.provider === 'google' ? 'text-text-muted' : 'text-[#00A4EF]'} ${calendarSyncing && calendarStatus?.provider === 'outlook' ? 'animate-spin' : ''}`} />
                      </div>
                      <div className="min-w-0 text-center">
                        <h5 className={`text-sm sm:text-base font-bold ${
                          calendarStatus?.connected && calendarStatus?.provider === 'outlook' ? 'text-accent' : calendarStatus?.connected && calendarStatus?.provider === 'google' ? 'text-text-muted' : 'text-text-primary'
                        }`}>
                          {calendarLoading === 'outlook' ? 'Processing...' : calendarSyncing && calendarStatus?.provider === 'outlook' ? 'Syncing...' : 'Outlook'}
                        </h5>
                      </div>
                    </div>
                  </motion.button>

                  {/* Google Calendar Sync Option - Nested Card */}
                  <motion.button
                    whileHover={{ scale: (calendarLoading || calendarSyncing || (calendarStatus?.connected && calendarStatus?.provider === 'outlook')) ? 1 : 1.02 }}
                    whileTap={{ scale: (calendarLoading || calendarSyncing || (calendarStatus?.connected && calendarStatus?.provider === 'outlook')) ? 1 : 0.98 }}
                    onClick={() => {
                      if (calendarStatus?.connected && calendarStatus?.provider === 'outlook') {
                        return;
                      }
                      if (calendarStatus?.connected && calendarStatus?.provider === 'google') {
                        setShowCalendarMenu(!showCalendarMenu);
                      } else {
                        handleCalendarConnect('google');
                      }
                    }}
                    disabled={calendarLoading || calendarSyncing || (calendarStatus?.connected && calendarStatus?.provider === 'outlook')}
                    className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-center min-h-[80px] sm:min-h-[100px] bg-card ${
                      calendarStatus?.connected && calendarStatus?.provider === 'google'
                        ? 'border-accent'
                        : calendarStatus?.connected && calendarStatus?.provider === 'outlook'
                          ? 'border-border-subtle opacity-50 cursor-not-allowed'
                          : 'border-border-subtle'
                    } ${(calendarLoading || calendarSyncing) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex flex-col sm:flex-row items-center justify-center">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center sm:mr-3 mb-1 sm:mb-0 flex-shrink-0">
                        <SafeIcon icon={calendarSyncing && calendarStatus?.provider === 'google' ? FiRefreshCw : FaGoogle} className={`w-6 h-6 sm:w-7 sm:h-7 ${calendarStatus?.connected && calendarStatus?.provider === 'google' ? 'text-accent' : calendarStatus?.connected && calendarStatus?.provider === 'outlook' ? 'text-text-muted' : 'text-[#4285F4]'} ${calendarSyncing && calendarStatus?.provider === 'google' ? 'animate-spin' : ''}`} />
                      </div>
                      <div className="min-w-0 text-center">
                        <h5 className={`text-sm sm:text-base font-bold ${
                          calendarStatus?.connected && calendarStatus?.provider === 'google' ? 'text-accent' : calendarStatus?.connected && calendarStatus?.provider === 'outlook' ? 'text-text-muted' : 'text-text-primary'
                        }`}>
                          {calendarLoading === 'google' ? 'Processing...' : calendarSyncing && calendarStatus?.provider === 'google' ? 'Syncing...' : 'Google'}
                        </h5>
                      </div>
                    </div>
                  </motion.button>

                  {/* ICS URL Calendar Option - Nested Card */}
                  <motion.button
                    whileHover={{ scale: icsLoading ? 1 : 1.02 }}
                    whileTap={{ scale: icsLoading ? 1 : 0.98 }}
                    onClick={() => {
                      if (icsSubscriptions.length > 0) {
                        setShowIcsMenu(!showIcsMenu);
                      } else {
                        setShowIcsModal(true);
                      }
                    }}
                    disabled={icsLoading}
                    className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-center min-h-[80px] sm:min-h-[100px] bg-card ${
                      icsSubscriptions.length > 0
                        ? 'border-accent'
                        : 'border-border-subtle'
                    } ${icsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex flex-col sm:flex-row items-center justify-center">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center sm:mr-3 mb-1 sm:mb-0 flex-shrink-0">
                        <SafeIcon icon={FiLink} className={`w-6 h-6 sm:w-7 sm:h-7 ${icsSubscriptions.length > 0 ? 'text-accent' : 'text-accent'}`} />
                      </div>
                      <div className="min-w-0 text-center">
                        <h5 className={`text-sm sm:text-base font-bold ${icsSubscriptions.length > 0 ? 'text-accent' : 'text-text-primary'}`}>
                          URL
                        </h5>
                      </div>
                    </div>
                  </motion.button>
                </div>

                {/* Full-row dropdown for connected Outlook calendar */}
                {showCalendarMenu && calendarStatus?.connected && calendarStatus?.provider === 'outlook' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-card rounded-xl border border-border-subtle overflow-hidden z-20"
                  >
                    <button
                      onClick={handleCalendarSync}
                      disabled={calendarSyncing}
                      className="w-full px-3 py-2.5 text-left hover:bg-app transition-colors duration-200 flex items-center gap-3 border-b border-border-subtle disabled:opacity-50 disabled:cursor-not-allowed rounded-t-xl"
                    >
                      <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 text-accent ${calendarSyncing ? 'animate-spin' : ''}`} />
                      <div className="flex-1">
                        <p className="font-semibold text-text-primary">Sync Now</p>
                        <p className="text-xs text-text-muted">
                          {calendarStatus.lastSynced
                            ? `Last synced: ${new Date(calendarStatus.lastSynced).toLocaleString()}`
                            : 'Not synced yet'}
                        </p>
                      </div>
                    </button>

                    <div
                      className="w-full px-3 py-2.5 flex items-center justify-between border-b border-border-subtle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiCalendar} className="w-5 h-5 text-accent" />
                        <div>
                          <p className="font-semibold text-text-primary text-sm">Add rounds to calendar</p>
                          <p className="text-xs text-text-muted">Sync scheduled rounds</p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !syncSettings.addRoundsToCalendar;
                          setSyncSettings(prev => ({ ...prev, addRoundsToCalendar: newValue }));
                          try {
                            await updateCalendarSyncSettings({ ...syncSettings, addRoundsToCalendar: newValue });
                          } catch (error) {
                            setSyncSettings(prev => ({ ...prev, addRoundsToCalendar: !newValue }));
                            console.error('Failed to update setting:', error);
                          }
                        }}
                        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                          syncSettings.addRoundsToCalendar ? 'bg-accent' : 'bg-subtle'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-card rounded-full shadow transition-transform duration-200 ${
                            syncSettings.addRoundsToCalendar ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    <button
                      onClick={handleCalendarDisconnect}
                      disabled={calendarLoading}
                      className="w-full px-3 py-2.5 text-left hover:bg-status-error/10 transition-colors duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed rounded-b-xl"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5 text-status-error" />
                      <div>
                        <p className="font-semibold text-status-error">Disconnect</p>
                        <p className="text-xs text-text-muted">Remove calendar sync</p>
                      </div>
                    </button>
                  </motion.div>
                )}

                {/* Full-row dropdown for connected Google calendar */}
                {showCalendarMenu && calendarStatus?.connected && calendarStatus?.provider === 'google' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-card rounded-xl border border-border-subtle overflow-hidden z-20"
                  >
                    <button
                      onClick={handleCalendarSync}
                      disabled={calendarSyncing}
                      className="w-full px-3 py-2.5 text-left hover:bg-app transition-colors duration-200 flex items-center gap-3 border-b border-border-subtle disabled:opacity-50 disabled:cursor-not-allowed rounded-t-xl"
                    >
                      <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 text-accent ${calendarSyncing ? 'animate-spin' : ''}`} />
                      <div className="flex-1">
                        <p className="font-semibold text-text-primary">Sync Now</p>
                        <p className="text-xs text-text-muted">
                          {calendarStatus.lastSynced
                            ? `Last synced: ${new Date(calendarStatus.lastSynced).toLocaleString()}`
                            : 'Not synced yet'}
                        </p>
                      </div>
                    </button>

                    <div
                      className="w-full px-3 py-2.5 flex items-center justify-between border-b border-border-subtle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiCalendar} className="w-5 h-5 text-accent" />
                        <div>
                          <p className="font-semibold text-text-primary text-sm">Add rounds to calendar</p>
                          <p className="text-xs text-text-muted">Sync scheduled rounds</p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !syncSettings.addRoundsToCalendar;
                          setSyncSettings(prev => ({ ...prev, addRoundsToCalendar: newValue }));
                          try {
                            await updateCalendarSyncSettings({ ...syncSettings, addRoundsToCalendar: newValue });
                          } catch (error) {
                            setSyncSettings(prev => ({ ...prev, addRoundsToCalendar: !newValue }));
                            console.error('Failed to update setting:', error);
                          }
                        }}
                        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                          syncSettings.addRoundsToCalendar ? 'bg-accent' : 'bg-subtle'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-card rounded-full shadow transition-transform duration-200 ${
                            syncSettings.addRoundsToCalendar ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    <button
                      onClick={handleCalendarDisconnect}
                      disabled={calendarLoading}
                      className="w-full px-3 py-2.5 text-left hover:bg-status-error/10 transition-colors duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed rounded-b-xl"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5 text-status-error" />
                      <div>
                        <p className="font-semibold text-status-error">Disconnect</p>
                        <p className="text-xs text-text-muted">Remove calendar sync</p>
                      </div>
                    </button>
                  </motion.div>
                )}

                {/* Full-row dropdown for ICS calendars */}
                {showIcsMenu && icsSubscriptions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-card rounded-xl border border-border-subtle overflow-hidden z-20"
                  >
                    <button
                      onClick={() => {
                        setShowIcsMenu(false);
                        setShowIcsModal(true);
                      }}
                      className="w-full px-3 py-2.5 text-left hover:bg-app transition-colors duration-200 flex items-center gap-3 border-b border-border-subtle"
                    >
                      <SafeIcon icon={FiPlus} className="w-5 h-5 text-accent" />
                      <div>
                        <p className="font-semibold text-text-primary">Add New Calendar</p>
                        <p className="text-xs text-text-muted">Connect another calendar URL</p>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setShowIcsMenu(false);
                        setShowConnectedIcsCalendars(true);
                      }}
                      className="w-full px-3 py-2.5 text-left hover:bg-app transition-colors duration-200 flex items-center gap-3 border-b border-border-subtle"
                    >
                      <SafeIcon icon={FiCalendar} className="w-5 h-5 text-accent" />
                      <div>
                        <p className="font-semibold text-text-primary">View Connected Calendars</p>
                        <p className="text-xs text-text-muted">{icsSubscriptions.length} calendar{icsSubscriptions.length > 1 ? 's' : ''} connected</p>
                      </div>
                    </button>

                    <button
                      onClick={async () => {
                        setShowIcsMenu(false);
                        const calendarText = icsSubscriptions.length > 1 ? 'all connected calendars' : `"${icsSubscriptions[0].name}"`;
                        if (!confirm(`Are you sure you want to disconnect ${calendarText}? This will also remove synced availability from ${icsSubscriptions.length > 1 ? 'these calendars' : 'this calendar'}.`)) {
                          return;
                        }
                        setIcsLoading(true);
                        try {
                          for (const sub of icsSubscriptions) {
                            await removeIcsSubscription(sub.id);
                          }
                          setCalendarSuccess(`Disconnected ${icsSubscriptions.length} calendar${icsSubscriptions.length > 1 ? 's' : ''}`);
                          const subscriptions = await getIcsSubscriptions();
                          setIcsSubscriptions(subscriptions);
                        } catch (error) {
                          setCalendarError('Failed to disconnect calendars');
                        } finally {
                          setIcsLoading(false);
                        }
                      }}
                      disabled={icsLoading}
                      className="w-full px-3 py-2.5 text-left hover:bg-status-error/10 transition-colors duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5 text-status-error" />
                      <div>
                        <p className="font-semibold text-status-error">
                          {icsSubscriptions.length > 1 ? 'Disconnect Calendars' : 'Disconnect Calendar'}
                        </p>
                        <p className="text-xs text-text-muted">Remove all URL calendars</p>
                      </div>
                    </button>
                  </motion.div>
                )}
              </div>
          </div>

          {/* Notification Preferences Section */}
          <div>
            <NotificationPreferencesSection
              pushStatus={pushStatus}
              pushLoading={pushLoading}
              onEnablePush={handleEnablePushNotifications}
              onDisablePush={handleDisablePushNotifications}
            />
          </div>

          {/* Appearance Section */}
          <AppearanceSection />

          {/* Danger zone - account deletion (Apple 5.1.1(v)) */}
          <div className="border border-status-error/30 rounded-lg p-5 bg-status-error/5">
            <h3 className="text-base font-bold text-text-primary mb-1">Danger zone</h3>
            <p className="text-sm text-text-secondary leading-relaxed mb-4">
              Permanently delete your TeeSync account and all of your data. This action cannot be undone.
            </p>
            <button
              type="button"
              onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
              className="px-4 py-2 border border-status-error text-status-error rounded-lg font-semibold text-sm hover:bg-status-error hover:text-white transition-colors inline-flex items-center gap-2"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              Delete account
            </button>
          </div>

          </div>

          {/* Version footer + super-admin-only 7-tap reveal for /admin.
              Version label is iOS-only - on web the underlying CapApp.getInfo()
              can't return a real value, so we hide the row entirely rather than
              show a stale fallback. The 7-tap admin reveal still works on web
              via an invisible hit area below. */}
          <div className="lg:col-span-2 mt-8 pt-6 border-t border-border-subtle text-center space-y-2">
            {isNative() ? (
              <button
                type="button"
                onClick={handleVersionTap}
                className="text-xs text-text-muted hover:text-text-secondary transition-colors select-none"
                aria-label="App version"
              >
                Version {versionInfo.version}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleVersionTap}
                className="block mx-auto w-24 h-4 opacity-0"
                aria-label="App version (hidden on web)"
                tabIndex={-1}
              />
            )}
            {adminVisible && (
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="block mx-auto text-xs text-accent hover:text-interactive-hover transition-colors"
              >
                Admin
              </button>
            )}
          </div>
        </PageTransition>
      </div>

      {/* ICS Add Calendar Modal */}
      {showIcsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-surface rounded-lg shadow-modal max-w-md w-full p-6 border border-border-subtle"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-text-primary">Add Calendar from URL</h3>
              <button
                onClick={() => {
                  setShowIcsModal(false);
                  setIcsName('');
                  setIcsUrl('');
                }}
                className="p-2 hover:bg-surface rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <p className="text-sm text-text-secondary mb-4">
              Add a calendar by entering its ICS/iCal URL. This works with Apple Calendar, Google Calendar shared links, Outlook.com, and most calendar applications.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Calendar Name
                </label>
                <input
                  type="text"
                  value={icsName}
                  onChange={(e) => setIcsName(e.target.value)}
                  placeholder="e.g., Work Calendar"
                  className="w-full px-3 py-2 border border-border-subtle rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors bg-card text-text-primary placeholder-text-muted"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Calendar URL
                </label>
                <input
                  type="url"
                  value={icsUrl}
                  onChange={(e) => setIcsUrl(e.target.value)}
                  placeholder="https://example.com/calendar.ics"
                  className="w-full px-3 py-2 border border-border-subtle rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-colors bg-card text-text-primary placeholder-text-muted"
                />
                <p className="text-xs text-text-muted mt-1">
                  The URL should end in .ics or be a valid calendar feed URL
                </p>
              </div>

              <div className="bg-app rounded-lg p-3 border border-border-subtle">
                <p className="text-xs font-medium text-text-secondary mb-2">Where to find your calendar URL:</p>
                <ul className="text-xs text-text-muted space-y-1">
                  <li><strong className="text-text-secondary">Apple:</strong> Calendar app → Share Calendar → Public Calendar</li>
                  <li><strong className="text-text-secondary">Google:</strong> Settings → Calendar → Integrate calendar → Secret address</li>
                  <li><strong className="text-text-secondary">Outlook:</strong> Calendar Settings → Shared calendars → Publish</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowIcsModal(false);
                  setIcsName('');
                  setIcsUrl('');
                }}
                className="flex-1 px-4 py-2 text-text-secondary bg-surface hover:bg-card rounded-md transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddIcsSubscription}
                disabled={icsLoading || !icsName.trim() || !icsUrl.trim()}
                className="flex-1 px-4 py-2 text-white bg-accent hover:bg-accent rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {icsLoading ? (
                  <>
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    Add Calendar
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Connected ICS Calendars Modal */}
      {showConnectedIcsCalendars && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-surface rounded-lg shadow-modal max-w-md w-full p-6 max-h-[80vh] overflow-y-auto border border-border-subtle"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-text-primary">Connected Calendars</h3>
              <button
                onClick={() => setShowConnectedIcsCalendars(false)}
                className="p-2 hover:bg-surface rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <p className="text-sm text-text-secondary mb-4">
              {icsSubscriptions.length} calendar{icsSubscriptions.length > 1 ? 's' : ''} connected via URL
            </p>

            <div className="space-y-3">
              {icsSubscriptions.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between p-3 bg-accent/10 rounded-lg border border-accent"
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-semibold text-accent truncate">{sub.name}</p>
                    <p className="text-xs text-accent truncate">{sub.url}</p>
                    {sub.last_synced_at && (
                      <p className="text-xs text-text-muted mt-1">
                        Last synced: {new Date(sub.last_synced_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleSyncIcsCalendar(sub.id)}
                      disabled={icsSyncing === sub.id || icsLoading}
                      className="p-2 text-accent hover:bg-accent/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Sync now"
                    >
                      <SafeIcon
                        icon={FiRefreshCw}
                        className={`w-4 h-4 ${icsSyncing === sub.id ? 'animate-spin' : ''}`}
                      />
                    </button>
                    <button
                      onClick={() => handleRemoveIcsSubscription(sub.id, sub.name)}
                      disabled={icsLoading}
                      className="p-2 text-status-error hover:bg-status-error/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Remove"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConnectedIcsCalendars(false)}
                className="flex-1 px-4 py-2 text-text-secondary bg-surface hover:bg-card rounded-md transition-colors font-medium"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowConnectedIcsCalendars(false);
                  setShowIcsModal(true);
                }}
                className="flex-1 px-4 py-2 text-white bg-accent hover:bg-accent rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                Add Calendar
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete account confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-card rounded-2xl shadow-xl border border-border-subtle w-full max-w-md p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-status-error/10 flex items-center justify-center">
                <SafeIcon icon={FiTrash2} className="w-5 h-5 text-status-error" />
              </div>
              <h2 className="text-lg font-bold text-text-primary">Delete your account?</h2>
            </div>
            <p className="text-sm text-text-secondary leading-relaxed mb-3">
              This permanently deletes your profile, any groups you own, scheduled rounds, support tickets, and all associated data. This cannot be undone.
            </p>
            <p className="text-sm text-text-secondary leading-relaxed mb-4">
              Type <span className="font-mono font-semibold text-text-primary">DELETE</span> below to confirm.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              disabled={deletingAccount}
              className="w-full px-3 py-2 border border-border-subtle rounded-md focus:ring-1 focus:ring-status-error focus:border-status-error bg-surface text-text-primary placeholder-text-muted text-sm mb-5"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                disabled={deletingAccount}
                className="flex-1 px-4 py-3 bg-card border border-border-subtle text-text-primary rounded-md hover:border-accent transition-colors font-medium min-h-[44px] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                className="flex-1 px-4 py-3 bg-status-error text-white rounded-md font-semibold transition-colors min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingAccount ? (
                  <>
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  'Delete account'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default Settings;
